import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendOrder,
	FrontendCompanyWithAdditionalInfomation,
	FrontendCompanyTestCategory,
} from "node-library/dist/portal";
import OrderRow from "./orderRow";
import { Card, Divider } from "@mui/material";
import { OrderTableSortColumn, SortOrder } from "node-library/dist/paginations";
import { NeutralColor } from "node-library/dist/colors";
import { SortArrowIcon } from "../../../../common-components/svgIcons";
import { CustomCheckbox } from "../../../../common-components/inputs";
import { GreenButton } from "../../../../common-components/button";
import { Severity } from "node-library/dist/constants";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import {
	formatDataForGoogleSheets,
	formatInstanceExcelData,
} from "../../../../helpers/googleSheetHelper";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;

	orders: Array<FrontendOrder>;
	user: FrontendUserInfo | undefined;

	sortBy: OrderTableSortColumn;
	// setSortBy: React.Dispatch<React.SetStateAction<OrderTableSortColumn>>;
	sortOrder: SortOrder;
	// setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>;

	selectedOrders: Set<string>;
	setSelectedOrders: React.Dispatch<React.SetStateAction<Set<string>>>;
	deleteOrder: (order: FrontendOrder) => void;

	goToViewOrderDetails: (order: FrontendOrder) => void;
	viewOrderCOC: (order: FrontendOrder) => void;

	setDiscardOrderConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setProceedAction: React.Dispatch<React.SetStateAction<() => void>>;
}

export default function ViewOrdersTableListView(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [selectAllCheckboxChecked, setSelectAllCheckboxChecked] =
		useState<boolean>(false);

	const {
		company,
		orders,
		sortBy,
		// setSortBy,
		sortOrder,
		// setSortOrder,
		selectedOrders,
		setSelectedOrders,
		deleteOrder,
		goToViewOrderDetails,
		viewOrderCOC,
		setDiscardOrderConfirmOpen,
		setProceedAction,
	} = props;

	async function handleBulkCopy() {
		const selected = orders.filter((order) =>
			selectedOrders.has(order.orderId),
		);

		if (selected.length === 0) {
			openSnackbar(
				translation("NoOrdersSelected"),
				Severity.ERROR,
				undefined,
				<RedErrorIcon />,
			);
			return;
		}

		const sortedOrders = [...selected].sort((a, b) => {
			let aVal: string | Date | undefined;
			let bVal: string | Date | undefined;

			switch (sortBy) {
				case OrderTableSortColumn.BARCODE_ID:
					aVal = a.orderBarcodeId ?? "";
					bVal = b.orderBarcodeId ?? "";
					break;
				case OrderTableSortColumn.INSERTED:
					aVal = a.inserted;
					bVal = b.inserted;
					break;
				default:
					aVal = "";
					bVal = "";
			}

			if (aVal === undefined || bVal === undefined) return 0;

			if (sortBy === OrderTableSortColumn.INSERTED) {
				const aTime = new Date(aVal as Date).getTime();
				const bTime = new Date(bVal as Date).getTime();
				return sortOrder === SortOrder.ASC ? aTime - bTime : bTime - aTime;
			} else {
				const result = String(aVal).localeCompare(String(bVal));
				return sortOrder === SortOrder.ASC ? result : -result;
			}
		});
		let allExcelRows: string[][] = [];
		for (const order of sortedOrders) {
			const sampleInstances = order.sampleInstances || [];
			const orderTestCategory: FrontendCompanyTestCategory =
				company.companyTestCategories.find((category) => {
					return order.orderCategoryId === category.testCategoryId;
				}) as FrontendCompanyTestCategory;

			const formatted = formatInstanceExcelData(
				order,
				company.testResultAndExpectationFields,
				orderTestCategory,
				sampleInstances,
				translation,
			);
			allExcelRows = [...allExcelRows, ...formatted];
		}
		const formattedText = formatDataForGoogleSheets(allExcelRows);
		await navigator.clipboard.writeText(formattedText);

		openSnackbar(
			translation("CopySuccessMsg"),
			Severity.SUCCESS,
			undefined,
			<GreenTickIcon />,
		);
	}
	const bulkCopyButton: React.ReactNode =
		selectedOrders.size > 0 ? (
			<div>
				<GreenButton
					key="bulk-copy-order-button"
					id="bulk-copy-order-button"
					children={translation("BulkCopy")}
					onClick={handleBulkCopy}
				></GreenButton>
			</div>
		) : (
			<></>
		);
	const headerItems = [
		{
			title: translation("Orders"),
		},
		{
			title: translation("Samples"),
		},
		{
			title: translation("Client"),
		},
		{
			title: translation("SubmissionTime"),
		},
		{
			title: translation("Status"),
		},
		{
			title: translation("Action"),
		},
	];

	const orderRows: Array<React.ReactNode> = orders
		? orders.map((order: FrontendOrder, index: number) => {
				return (
					<OrderRow
						key={order.orderId}
						order={order}
						user={props.user}
						selected={props.selectedOrders.has(order.orderId)}
						setSelected={(selected: boolean) => {
							const updated = new Set(props.selectedOrders);
							if (selected) {
								updated.add(order.orderId);
								if (
									orders.every((order) => selectedOrders.has(order.orderId))
								) {
									setSelectAllCheckboxChecked(true);
								}
							} else {
								updated.delete(order.orderId);
								setSelectAllCheckboxChecked(false);
							}
							props.setSelectedOrders(updated);
						}}
						goToViewOrderDetails={() => goToViewOrderDetails(order)}
						deleteOrder={() => {
							setProceedAction(() => () => deleteOrder(order));
							setDiscardOrderConfirmOpen(true);
						}}
					/>
				);
			})
		: [];

	const headerRowItems: React.ReactNode = headerItems.map((item) => {
		return (
			<div key={item.title} style={{ display: "flex", gap: "10px" }}>
				<Divider orientation="vertical" flexItem></Divider>
				<div
					className="typography-body-01"
					style={{
						color: NeutralColor.NEUTRAL_GREY_500,
						display: "flex",
						alignItems: "center",
						gap: "5px",
					}}
				>
					{item.title}
					{/* {item.sortColumnBy && (
						<SortArrowIcon
							sortOrder={sortOrder}
							notInUse={sortBy !== item.sortColumnBy}
						/>
					)} */}
				</div>
			</div>
		);
	});

	const sampleRowContainer: React.ReactNode = (
		<Card className="common-card card-medium-padding">{orderRows}</Card>
	);

	return (
		<div>
			<div style={{ minHeight: "48px", marginBottom: "16px" }}>
				{bulkCopyButton}
			</div>
			<div className=" order-list-view-row order-list-view-header">
				{
					<CustomCheckbox
						className={``}
						label={``}
						checked={selectAllCheckboxChecked}
						setChecked={() => {
							const newState: boolean = !selectAllCheckboxChecked;
							setSelectAllCheckboxChecked(newState);
							if (newState) {
								const all = new Set(orders.map((o) => o.orderId));
								setSelectedOrders(all);
							} else {
								setSelectedOrders(new Set());
							}
						}}
					/>
				}
				<div className="order-list-view-item-row">{headerRowItems}</div>
			</div>
			{sampleRowContainer}
		</div>
	);
}
